import { RightOutlined } from '@ant-design/icons';
import { Species, SpeciesSurveyDataV1 } from '@omniafishing/core';
import React, { useCallback } from 'react';
import { useResponsive } from '../../../../../hooks/use_responsive';
import { determineLunkerYear } from '../../../../../lib/species_survey_data';
import { LoadableChartCpue } from './loadable_chart_cpue';
import { LunkerPotential } from './lunker_potential';
import { SvgAbundance } from './svg_abundance';

export const abundanceTextMap = {
  1: 'Low',
  2: 'Below avg.',
  3: 'Above avg.',
  4: 'High',
};
export const abundanceTextColorClassMap = {
  1: 'text-[#FF2A00]',
  2: 'text-[#C800FF]',
  3: 'text-[#00AD2B]',
  4: 'text-[#00AD2B]',
};

export const formatLength = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
};

interface SpeciesCardProps {
  species: Species;
  speciesData: SpeciesSurveyDataV1['species'][keyof SpeciesSurveyDataV1['species']];
  onClick: (species: Species) => void;
}

export const SpeciesCard = (props: SpeciesCardProps) => {
  const { species, speciesData, onClick } = props;

  const { current_status } = speciesData;
  const { abundance_quartile, length_statistics } = current_status;
  const lunkerYear = determineLunkerYear(species.display_name, speciesData);

  const { isMobile } = useResponsive();

  const onSpeciesClick = useCallback(() => {
    onClick(species);
  }, [onClick]);

  return (
    <div className="bg-white p-3 rounded-lg relative pt-6">
      <div
        className="bg-[#6B71721C] rounded-full flex items-center justify-center w-8 h-8 absolute top-3 right-3 z-10 cursor-pointer"
        onClick={onSpeciesClick}
      >
        <RightOutlined />
      </div>

      <div className="grid grid-cols-2 gap-2 cursor-pointer" onClick={onSpeciesClick}>
        <div className="flex flex-col gap-2 items-start">
          {lunkerYear && <LunkerPotential year={lunkerYear} />}
          <h3 className="font-bold text-xl font-sans">{species.display_name}</h3>
          {abundance_quartile && (
            <div className="mb-3">
              <p className={`text-xs font-bold ${abundanceTextColorClassMap[abundance_quartile]}`}>
                {abundanceTextMap[abundance_quartile]}
              </p>
              <p className="text-[11px] text-[#6B7172] mb-2">Current Abundance</p>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4].map((quartile) => (
                  <SvgAbundance
                    key={quartile}
                    className={
                      quartile <= abundance_quartile
                        ? abundanceTextColorClassMap[abundance_quartile]
                        : 'text-[#D5D7D9]'
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <img src={species.image} alt={`picture of ${species.display_name}`} />
          <div className="flex text-[11px]">
            <div className="w-1/2 pr-3 flex flex-col">
              <span className="font-bold">
                {formatLength(length_statistics.min)}–{formatLength(length_statistics.max)} inches
              </span>
              <span>Length Range</span>
            </div>
            <div className="w-1/2 pl-3 border-l flex flex-col">
              <span className="font-bold"> {formatLength(length_statistics.avg)} inches</span>
              <span>Avg. Length</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex sm:justify-center">
        <div>
          <p className="text-[10px] text-[#6B7172]">Historical CPUE</p>
          <LoadableChartCpue speciesData={speciesData} width={isMobile ? 340 : 470} height={200} />
        </div>
      </div>
    </div>
  );
};
